import MapboxDraw, { DrawCustomMode, MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, Point } from "geojson";
import geojsonRbush from "geojson-rbush";
import { singleSource as dijkstraSingleSource } from "graphology-shortest-path/dijkstra";
import { edgePathFromNodePath } from "graphology-shortest-path/utils";
import minBy from "lodash/minBy";
import type mapboxgl from "mapbox-gl";

import { lineString as turfLineString, point as turfPoint } from "fond/turf";

import { buildPathCoordinates, EdgeId, NodeId, PathGraph } from "./pathGraph";
import { ensurePreviewCircleLayer, ensurePreviewLayer, removePreviewLayer, setPreviewFeatures } from "./snapPreview";

const { constants: Constants } = MapboxDraw;

// We distinguish between the 'preview' path (the portion of the line the user has already defined),
// and the 'tentative' path (a potential extension of the path to the currently hovered node).
const PATH_SOURCE_ID = "shortest-path-preview-source";
const PATH_LAYER_ID = "shortest-path-preview-layer";
const TENTATIVE_SOURCE_ID = "shortest-path-tentative-source";
const TENTATIVE_LAYER_ID = "shortest-path-tentative-layer";
const NODE_SOURCE_ID = "shortest-path-node-source";
const NODE_LAYER_ID = "shortest-path-node-layer";

const SNAP_THRESHOLD_PX = 15;

/**
 * Return a spatial index of nodes in the graph.
 */
function buildNodeIndex(graph: PathGraph) {
  const index = geojsonRbush<Point, { nodeId: NodeId }>();
  index.load(graph.mapNodes((nodeId, attrs) => turfPoint(attrs.coord, { nodeId })));
  return index;
}

type NodeIndex = ReturnType<typeof buildNodeIndex>;

interface PathBuilding {
  // The start node of the path
  startNodeId: NodeId;

  // The current end node of the path
  currentTailNodeId: NodeId;

  // The edges forming the path so far.
  committedEdgeIds: EdgeId[];

  // single-source shortest paths from currentTailNodeId.
  shortestPaths: Record<NodeId, NodeId[]>;
}

interface ShortestPathState {
  graph: PathGraph | null;
  nodeIndex: NodeIndex | null;
  building: PathBuilding | null;
  lastHoveredNodeId: NodeId | null;
  singleFeature: boolean;
}

/**
 * Finds the graph node nearest to the given screen point, within `thresholdPx`.
 */
function findNearestNodeByPixel(state: ShortestPathState, map: mapboxgl.Map, mouseX: number, mouseY: number, thresholdPx: number): NodeId | null {
  if (!state.nodeIndex) return null;

  const nw = map.unproject([mouseX - thresholdPx, mouseY - thresholdPx]);
  const se = map.unproject([mouseX + thresholdPx, mouseY + thresholdPx]);
  const minLng = Math.min(nw.lng, se.lng);
  const maxLng = Math.max(nw.lng, se.lng);
  const minLat = Math.min(nw.lat, se.lat);
  const maxLat = Math.max(nw.lat, se.lat);

  const thresholdSq = thresholdPx * thresholdPx;
  const candidates = state.nodeIndex
    .search([minLng, minLat, maxLng, maxLat])
    .features.map((f) => {
      const [lng, lat] = f.geometry.coordinates;
      const p = map.project([lng, lat]);
      const dx = p.x - mouseX;
      const dy = p.y - mouseY;
      return { nodeId: f.properties!.nodeId, distSq: dx * dx + dy * dy };
    })
    .filter((c) => c.distSq < thresholdSq);

  return minBy(candidates, (c) => c.distSq)?.nodeId ?? null;
}

/**
 * Render a preview line based on the currently hovered node.
 */
function renderHoveredNode(map: mapboxgl.Map, graph: PathGraph, nodeId: NodeId | null): void {
  if (!nodeId) {
    setPreviewFeatures(map, NODE_SOURCE_ID, []);
    return;
  }
  if (!graph.hasNode(nodeId)) return;
  setPreviewFeatures(map, NODE_SOURCE_ID, [turfPoint(graph.getNodeAttribute(nodeId, "coord"))]);
}

/**
 * Render a path to the given mapbox source.
 */
function renderPath(map: mapboxgl.Map, sourceId: string, graph: PathGraph, sourceNodeId: NodeId, edgeIds: EdgeId[]): void {
  const coords = buildPathCoordinates(graph, sourceNodeId, edgeIds);
  setPreviewFeatures(map, sourceId, coords.length < 2 ? [] : [turfLineString(coords)]);
}

/**
 * Render a dashed tentative line from the current tail to the hovered node.
 */
function renderTentativeFromHover(state: ShortestPathState, map: mapboxgl.Map, hovered: NodeId | null): void {
  if (!state.graph || !state.building || !hovered || hovered === state.building.currentTailNodeId) return;

  // Show a tentative line from the tail to the hovered node via the shortest path.
  const tail = state.building.currentTailNodeId;
  let edgeIds: EdgeId[] = [];
  const nodePath = state.building.shortestPaths[hovered];
  if (nodePath && nodePath.length >= 2) {
    edgeIds = edgePathFromNodePath(state.graph, nodePath);
  }

  renderPath(map, TENTATIVE_SOURCE_ID, state.graph, tail, edgeIds);
}

/**
 * Commit the accumulated path. Emits one feature per edge by default, or a single merged
 * feature when `state.singleFeature` is true.
 */
function commitAccumulatedPath(mode: MapboxDraw.DrawCustomModeThis, state: ShortestPathState): Feature[] {
  if (!state.graph || !state.building || state.building.committedEdgeIds.length === 0) return [];

  if (state.singleFeature) {
    const coords = buildPathCoordinates(state.graph, state.building.startNodeId, state.building.committedEdgeIds);
    if (coords.length < 2) return [];
    const newFeature = mode.newFeature(turfLineString(coords));
    mode.addFeature(newFeature);
    return [newFeature.toGeoJSON() as Feature];
  }

  const created: Feature[] = [];
  for (const edgeId of state.building.committedEdgeIds) {
    const edge = state.graph.getEdgeAttributes(edgeId)!;
    const newFeature = mode.newFeature(turfLineString(edge.coordinates, { sourcePathId: edge.featureId }));
    mode.addFeature(newFeature);
    created.push(newFeature.toGeoJSON() as Feature);
  }

  return created;
}

/**
 * Finalize and commit an in-progress path.
 *
 * This emits each accumulated edge as a (separate) draw feature.
 */
function commitAndExit(mode: MapboxDraw.DrawCustomModeThis, state: ShortestPathState): void {
  const created = commitAccumulatedPath(mode, state);
  resetPathState(state);
  clearPreviewLayers(mode.map);
  if (created.length > 0) {
    mode.map.fire(Constants.events.CREATE, { features: created });
  }

  // If you double-click the end node then mapbox will zoom you in. Strangely, this doesn't happen
  // if you double-click the end node in the line draw mode, but I'm unable to replicate that behaviour
  // here directly. As a workaround, we disable zoom on the next double-click with a short timeout. This
  // is definitely a bit hacky, but it seems to work.
  const { map } = mode;
  const suppressZoom = (e: mapboxgl.MapMouseEvent) => e.preventDefault();
  map.once("dblclick", suppressZoom);
  setTimeout(() => map.off("dblclick", suppressZoom), 500);

  mode.changeMode(Constants.modes.SIMPLE_SELECT);
}

/**
 * Begin a new path from `hovered`.
 */
function startPath(state: ShortestPathState, map: mapboxgl.Map, hovered: NodeId): void {
  if (!state.graph) return;
  state.building = {
    startNodeId: hovered,
    currentTailNodeId: hovered,
    committedEdgeIds: [],
    // Pre-compute shortest paths from the start node
    shortestPaths: dijkstraSingleSource(state.graph, hovered, "length"),
  };
  renderHoveredNode(map, state.graph, hovered);
}

/**
 * Extend the in-progress path to the clicked node via the shortest path in the graph.
 *
 * If there is no path from the current tail to the clicked node, then this function will do nothing.
 */
function extendPath(state: ShortestPathState, map: mapboxgl.Map, hovered: NodeId): void {
  if (!state.graph || !state.building) return;

  const nodePath = state.building.shortestPaths[hovered];
  if (!nodePath || nodePath.length < 2) return; // unreachable from current tail

  const edgeIds = edgePathFromNodePath(state.graph, nodePath);
  state.building.committedEdgeIds = [...state.building.committedEdgeIds, ...edgeIds];
  state.building.currentTailNodeId = hovered;
  state.building.shortestPaths = dijkstraSingleSource(state.graph, hovered, "length");
  renderPath(map, PATH_SOURCE_ID, state.graph, state.building.startNodeId, state.building.committedEdgeIds);
  setPreviewFeatures(map, TENTATIVE_SOURCE_ID, []);
  renderHoveredNode(map, state.graph, hovered);
}

type PreviewStyleOverrides = NonNullable<NonNullable<MapboxDrawOptions["shortestPath"]>["previewStyle"]>;

function setupPreviewLayers(map: mapboxgl.Map, overrides?: PreviewStyleOverrides): void {
  ensurePreviewLayer(map, PATH_SOURCE_ID, PATH_LAYER_ID, {
    "line-color": "#1976d2",
    "line-width": 6,
    "line-opacity": 0.7,
    ...overrides?.path,
  });
  ensurePreviewLayer(map, TENTATIVE_SOURCE_ID, TENTATIVE_LAYER_ID, {
    "line-color": "#1976d2",
    "line-width": 4,
    "line-opacity": 0.5,
    "line-dasharray": [2, 2],
    ...overrides?.tentative,
  });
  ensurePreviewCircleLayer(map, NODE_SOURCE_ID, NODE_LAYER_ID, {
    "circle-radius": 6,
    "circle-color": "#1976d2",
    "circle-stroke-color": "#ffffff",
    "circle-stroke-width": 2,
    ...overrides?.node,
  });
}

function teardownPreviewLayers(map: mapboxgl.Map): void {
  removePreviewLayer(map, PATH_SOURCE_ID, PATH_LAYER_ID);
  removePreviewLayer(map, TENTATIVE_SOURCE_ID, TENTATIVE_LAYER_ID);
  removePreviewLayer(map, NODE_SOURCE_ID, NODE_LAYER_ID);
}

function clearPreviewLayers(map: mapboxgl.Map): void {
  setPreviewFeatures(map, PATH_SOURCE_ID, []);
  setPreviewFeatures(map, TENTATIVE_SOURCE_ID, []);
  setPreviewFeatures(map, NODE_SOURCE_ID, []);
}

function resetPathState(state: ShortestPathState): void {
  state.building = null;
  state.lastHoveredNodeId = null;
}

/**
 * A custom draw mode for building a path along a chain of shortest paths through a precomputed graph.
 *
 * The first click sets the start node for the path; each subsequent click extends the path
 * via the shortest path to the newly clicked node. Clicking the current tail commits the accumulated
 * path, committing one draw feature per edge in the path.
 *
 * Each committed draw feature has a `sourcePathId` that specifies the source feature that edge came from.
 * The caller is responsible for populating `shortestPath.graph` on the draw options with a `PathGraph`.
 */
const shortestPathFeature: DrawCustomMode<ShortestPathState> = {
  onSetup(): ShortestPathState {
    this.updateUIClasses({ mouse: "add" });
    this.map.doubleClickZoom.disable();
    setupPreviewLayers(this.map, this._ctx.options.shortestPath?.previewStyle);
    const graph = this._ctx.options.shortestPath?.graph ?? null;

    return {
      graph,
      nodeIndex: graph ? buildNodeIndex(graph) : null,
      building: null,
      lastHoveredNodeId: null,
      singleFeature: this._ctx.options.shortestPath?.singleFeature ?? false,
    };
  },

  onMouseMove(state: ShortestPathState, event: MapboxDraw.MapMouseEvent) {
    if (!state.graph) return;

    // We show a hover circle when the user moves their mouse near a node.
    const hovered = findNearestNodeByPixel(state, this.map, event.point.x, event.point.y, SNAP_THRESHOLD_PX);
    if (hovered === state.lastHoveredNodeId) return;
    state.lastHoveredNodeId = hovered;
    renderHoveredNode(this.map, state.graph, hovered);
    renderTentativeFromHover(state, this.map, hovered);
  },

  onClick(state: ShortestPathState, event: MapboxDraw.MapMouseEvent) {
    if (!state.graph) return;
    const hovered = findNearestNodeByPixel(state, this.map, event.point.x, event.point.y, SNAP_THRESHOLD_PX);
    if (!hovered) return;

    // User isn't building a path yet, so start a new path from the clicked node.
    if (!state.building) {
      startPath(state, this.map, hovered);
      return;
    }

    // User clicked the current tail node
    if (hovered === state.building.currentTailNodeId) {
      // User double-clicked the start node => do nothing
      if (state.building.committedEdgeIds.length === 0) return;

      // User clicked the tail => commit the path.
      commitAndExit(this, state);
      return;
    }

    // Otherwise the user clicked on/near a node; try to extend the path to it.
    extendPath(state, this.map, hovered);
  },

  onKeyUp(state: ShortestPathState, event: KeyboardEvent) {
    if (event.key !== "Escape" || !state.building) return;
    resetPathState(state);
    clearPreviewLayers(this.map);
  },

  onStop() {
    this.map.doubleClickZoom.enable();
    teardownPreviewLayers(this.map);
  },

  toDisplayFeatures(_state, geojson, display) {
    display(geojson);
  },
};

export default shortestPathFeature;
